import React from 'react';
import { fmtNum } from '../../utils/formatArcaNumbers';
import { projectMonthly } from '../../utils/monthlyProjection';
import { PlanReturnView } from '../../utils/transferReturns';
import { periodLabel } from './projectionLegend';

interface NewPlanRetrospectiveCardProps {
	/** Aportación mensual con la que se simula el pasado. */
	monthlyContribution: number;
	/** Rentabilidad del plan decidida por comparsa: la tasa mensual neta y su periodo. */
	planReturn: PlanReturnView;
	planName: string;
	translate: (key: string) => string;
}

/**
 * "Los últimos N años · ¿Y si hubieras empezado antes?": cuánto tendría ya ahorrado el
 * usuario si hubiera abierto ESTE plan hace N años aportando lo mismo cada mes, siendo N el
 * periodo del dato (no 5 fijos: con un dato de 1 año solo se puede mirar 1 año atrás). Mes a
 * mes con la tasa neta de comparsa, aportación a final de mes.
 */
const NewPlanRetrospectiveCard: React.FC<NewPlanRetrospectiveCardProps> = ({
	monthlyContribution,
	planReturn,
	planName,
	translate
}) => {
	// Sin aportación no hay nada que acumular en el pasado: no mostramos la card.
	if (monthlyContribution <= 0) return null;

	const years = planReturn.periodYears;
	const accrued = projectMonthly(0, monthlyContribution, planReturn.monthlyPct, years);
	const contributed = monthlyContribution * 12 * years;
	const gain = Math.max(0, accrued - contributed);

	const eyebrow =
		years === 1
			? translate('individual.newPlanRetro.eyebrowOne') || 'El último año'
			: (translate('individual.comparison.result.eyebrow') || 'Los últimos {years} años').replace(
					'{years}',
					String(years)
				);

	return (
		<div className="surface-card bg-primary/5 border border-primary/20 p-4 space-y-2">
			<div>
				<p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
					{eyebrow}
				</p>
				<h3 className="text-base font-bold">
					{translate('individual.newPlanRetro.question') || '¿Y si hubieras empezado antes?'}
				</h3>
			</div>
			<p className="text-sm text-muted-foreground leading-snug">
				{(
					translate('individual.newPlanRetro.caption') ||
					'Si hubieras abierto {plan} hace {period} aportando {monthly}/mes, hoy ya tendrías'
				)
					.replace('{plan}', planName)
					.replace('{period}', periodLabel(translate, years))
					.replace('{monthly}', `${fmtNum(monthlyContribution, 0)} €`)}{' '}
				<span className="text-xl font-bold font-heading text-primary align-middle">
					{fmtNum(accrued, 0)} €
				</span>{' '}
				{(
					translate('individual.newPlanRetro.gain') ||
					'ahorrados, de los cuales {gain} € serían pura rentabilidad.'
				).replace('{gain}', fmtNum(gain, 0))}
			</p>
		</div>
	);
};

export default NewPlanRetrospectiveCard;
